import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface MoneyInputProps
  extends Omit<React.ComponentProps<"input">, "value" | "defaultValue" | "onChange" | "type"> {
  /** Safe integer currency minor units: USD cents, JPY yen, KWD fils. `null` is empty, distinct from zero. */
  value: number | null;
  onValueChange: (minorUnits: number | null) => void;
  /** ISO 4217 code; callers pass the organization's currency. */
  currency?: string;
  locale?: string;
  allowNegative?: boolean;
}

function moneyFormat(currency: string, locale: string) {
  const currencyFormatter = new Intl.NumberFormat(locale, { style: "currency", currency });
  const currencyParts = currencyFormatter.formatToParts(0);
  const currencyIndex = currencyParts.findIndex((part) => part.type === "currency");
  const firstNumberIndex = currencyParts.findIndex((part) => part.type === "integer");
  const lastNumberIndex = currencyParts.reduce((last, part, index) =>
    ["integer", "group", "decimal", "fraction"].includes(part.type) ? index : last, firstNumberIndex);
  const position = currencyIndex < firstNumberIndex ? "start" : "end";
  const currencyText = currencyParts.slice(
    position === "start" ? currencyIndex : lastNumberIndex + 1,
    position === "start" ? firstNumberIndex : currencyIndex + 1,
  ).map((part) => part.value).join("");
  const options = currencyFormatter.resolvedOptions();
  const fractionDigits = options.maximumFractionDigits ?? 2;
  const numericOptions = { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits };
  const grouped = new Intl.NumberFormat(locale, numericOptions);
  const ungrouped = new Intl.NumberFormat(locale, { ...numericOptions, useGrouping: false });
  const digitFormatter = new Intl.NumberFormat(locale, { useGrouping: false });
  const digitPairs = Array.from({ length: 10 }, (_, digit) => [digitFormatter.format(digit), String(digit)]);
  const grouping = grouped.formatToParts(123456789012345n);
  const integerGroups = grouping.filter((part) => part.type === "integer").map((part) => [...part.value].length);
  const punctuation = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).formatToParts(-1.1);
  return {
    currency: options.currency ?? currency,
    currencyText,
    position,
    fractionDigits,
    grouped,
    ungrouped,
    digitPairs,
    decimal: punctuation.find((part) => part.type === "decimal")?.value ?? ".",
    minus: punctuation.find((part) => part.type === "minusSign")?.value ?? "-",
    group: grouping.find((part) => part.type === "group")?.value,
    primaryGroupSize: integerGroups[integerGroups.length - 1] ?? 3,
    secondaryGroupSize: integerGroups[integerGroups.length - 2] ?? 3,
  };
}

type MoneyFormat = ReturnType<typeof moneyFormat>;

// Parse decimal text directly into minor units; no floating point or two-decimal intermediate.
function parseDraft(text: string, format: MoneyFormat, allowNegative: boolean): number | null | undefined {
  let next = text.trim().replace(/[\u061c\u200e\u200f]/g, "");
  for (const [local, ascii] of format.digitPairs) next = next.split(local).join(ascii);
  const negative = next.startsWith(format.minus) || next.startsWith("-");
  if (negative) {
    if (!allowNegative) return undefined;
    next = next.slice(next.startsWith(format.minus) ? format.minus.length : 1);
  }
  if (format.group && /\s/u.test(format.group)) next = next.replace(/[ \u00a0\u202f]/g, format.group);
  const sections = next.split(format.decimal);
  if (sections.length > 2 || (sections.length === 2 && format.fractionDigits === 0)) return undefined;
  const fraction = sections[1] ?? "";
  if (!/^\d*$/.test(fraction) || fraction.length > format.fractionDigits) return undefined;
  const groups = format.group ? sections[0].split(format.group) : [sections[0]];
  if (groups.some((group) => !/^\d*$/.test(group))) return undefined;
  if (groups.length > 1 && (
    groups[0].length < 1 || groups[0].length > format.secondaryGroupSize ||
    groups[groups.length - 1].length !== format.primaryGroupSize ||
    groups.slice(1, -1).some((group) => group.length !== format.secondaryGroupSize)
  )) return undefined;
  const integer = groups.join("");
  if (!integer && !fraction) return null;
  const scaled = (integer + fraction.padEnd(format.fractionDigits, "0")).replace(/^0+/, "") || "0";
  if (scaled.length > 16) return undefined;
  const minorUnits = BigInt(scaled);
  if (minorUnits > BigInt(Number.MAX_SAFE_INTEGER)) return undefined;
  return Number(negative ? -minorUnits : minorUnits);
}

function formatAmount(value: number | null, format: MoneyFormat, useGrouping = true) {
  if (value === null || !Number.isSafeInteger(value)) return "";
  const amount = BigInt(value);
  const magnitude = amount < 0n ? -amount : amount;
  const scale = 10n ** BigInt(format.fractionDigits);
  const whole = magnitude / scale;
  const signedWhole = amount < 0n ? (whole === 0n ? -0 : -whole) : whole;
  const fraction = (magnitude % scale).toString().padStart(format.fractionDigits, "0");
  const localFraction = fraction.replace(/\d/g, (digit) => format.digitPairs[Number(digit)][0]);
  return (useGrouping ? format.grouped : format.ungrouped).formatToParts(signedWhole)
    .map((part) => part.type === "fraction" ? localFraction : part.value).join("");
}

/**
 * Money field with safe integer currency minor units, parsed without floating point arithmetic.
 * While focused the field shows exactly what was typed; on blur it normalises to the currency's
 * decimals. Digits are tabular so columns of amounts line up.
 */
export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onValueChange, currency = "USD", locale = "en-US", allowNegative = false, inputMode = allowNegative ? "text" : "decimal", className, style, hidden, onBlur, onFocus, dir, "aria-describedby": describedBy, ...rest },
  ref,
) {
  const format = React.useMemo(() => moneyFormat(currency, locale), [currency, locale]);
  const formatKey = `${currency}|${locale}`;
  const [draft, setDraft] = React.useState<{ text: string; formatKey: string; minorUnits: number | null } | null>(null);
  const denominationId = React.useId();
  const currencyRef = React.useRef<HTMLSpanElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => inputRef.current, []);
  const [inputInvisible, setInputInvisible] = React.useState(false);
  const adornmentKey = `${format.position}|${format.currencyText}`;
  const [measurement, setMeasurement] = React.useState<{ key: string; width: number } | null>(null);
  React.useLayoutEffect(() => {
    const element = currencyRef.current;
    if (!element) return;
    const measure = () => {
      const width = element.getBoundingClientRect().width;
      if (width > 0) setMeasurement({ key: adornmentKey, width });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [adornmentKey]);
  const currencyPadding = measurement?.key === adornmentKey ? {
    [format.position === "start" ? "paddingInlineStart" : "paddingInlineEnd"]: `calc(0.75rem + ${measurement.width}px)`,
  } : {};
  React.useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const refresh = () => {
      const computed = getComputedStyle(input);
      setInputInvisible(input.hidden || computed.display === "none" ||
        computed.visibility === "hidden" || computed.visibility === "collapse");
    };
    refresh();
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(refresh);
    resize?.observe(input);
    const attributes = new MutationObserver(refresh);
    attributes.observe(input, { attributes: true });
    attributes.observe(document.documentElement, { attributes: true });
    window.addEventListener("resize", refresh);
    return () => { resize?.disconnect(); attributes.disconnect(); window.removeEventListener("resize", refresh); };
  }, [hidden, className, style?.display, style?.visibility]);
  React.useLayoutEffect(() => {
    if (draft && (draft.formatKey !== formatKey || draft.minorUnits !== value)) {
      setDraft({ text: formatAmount(value, format, false), formatKey, minorUnits: value });
    }
  }, [draft, value, formatKey, format]);
  const shown = draft?.formatKey === formatKey && draft.minorUnits === value
    ? draft.text : formatAmount(value, format, draft === null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    const minorUnits = parseDraft(next, format, allowNegative);
    if (minorUnits === undefined) return;
    setDraft({ text: next, formatKey, minorUnits });
    onValueChange(minorUnits);
  };

  return (
    <div className="relative" dir={dir} hidden={hidden}
      style={{ display: hidden || style?.display === "none" ? "none" : undefined, visibility: style?.visibility }}>
      <span id={denominationId} className="sr-only" hidden={inputInvisible}>{format.currency}</span>
      <span ref={currencyRef} aria-hidden="true" style={{ display: inputInvisible ? "none" : undefined }} className={cn(
        "pointer-events-none absolute inset-y-0 z-10 flex items-center whitespace-pre text-sm text-muted-foreground",
        format.position === "start" ? "start-3" : "end-3",
      )}>
        {format.currencyText}
      </span>
      <Input
        {...rest}
        ref={inputRef}
        dir={dir}
        hidden={hidden}
        aria-describedby={[describedBy, denominationId].filter(Boolean).join(" ")}
        type="text"
        inputMode={inputMode}
        autoComplete="off"
        style={{ ...currencyPadding, ...style }}
        value={shown}
        onChange={handleChange}
        onFocus={(event) => {
          setDraft({ text: formatAmount(value, format, false), formatKey, minorUnits: value });
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setDraft(null);
          onBlur?.(event);
        }}
        className={cn("text-end tabular-nums", format.position === "start" ? "ps-8" : "pe-8", className)}
      />
    </div>
  );
});
